import { createDoc, openDoc } from '../../helpers/docHelpers';
import { computeOpenBehavior } from '../../helpers/navHelpers';
import { useViewMode } from '../../state/ViewModeContext';

/**
 * EditorPlaceholder - Shows loading only when a doc is expected to render,
 * otherwise shows the create document UI
 */
function EditorPlaceholder({ 
  pane,
  currentProject,
  currentUserId,
  itemsMap,
  setItemsMap,
  allTabs,
  tabs,
  setTabs,
  activeTabId,
  setActiveTabId,
  setLeftActiveTabId,
  setRightActiveTabId,
  setActivePane,
  editorInstances,
  editorRef,
  isLoading = false,
  isProjectReadOnly = false
}) {
  const { isEditable } = useViewMode();

  // While tabs hydrate, keep a neutral placeholder and let the toast channel
  // communicate loading progress in the editor's normal toast location.
  if (isLoading) {
    return (
      <div className="editorPlaceholder">
        <div>Select or drag a document here to open</div>
      </div>
    );
  }

  // Viewer / read-only placeholder — no create button
  if (!isEditable || isProjectReadOnly) {
    return (
      <div className="editorPlaceholder">
        <div>Select or drag a document here to open</div>
      </div>
    );
  }

  // Editor placeholder — with create button
  return (
    <div className="editorPlaceholder">
      <div>Select or create a new document to get started</div>
      <button
        className="primary"
        onClick={async () => {
          if (!currentProject || !currentUserId) return;
          
          // Create new doc in root of project
          const newDoc = await createDoc(
            currentProject.id,
            null, // parent_id = null for root
            currentUserId,
            itemsMap,
            setItemsMap
          );
          
          if (newDoc) {
            const beh = computeOpenBehavior({ type: 'doc', tabs: allTabs || tabs, activePane: pane });
            const targetPane = beh?.preferredPane === 'right' ? 'right' : 'left';
            const setPaneActiveTabId = targetPane === 'right' ? setRightActiveTabId : setLeftActiveTabId;
            const setFocusedTabId = (tabId) => {
              setPaneActiveTabId?.(tabId);
              setActiveTabId?.(tabId);
            };
            
            // Open doc using helper, which handles tab creation and activation
            // Use allTabs (full tabs array) not the filtered tabs
            // Use 'new' mode since this pane is empty (tabs.length === 0)
            openDoc(
              newDoc,
              allTabs || tabs,
              setTabs,
              activeTabId,
              setFocusedTabId,
              editorRef,
              'new'
            );
            
            // Set pane on the newly created tab
            setTabs(prev => prev.map(t => 
              t.docId === newDoc.id && !t.pane ? { ...t, pane: targetPane } : t
            ));
            
            // Focus the pane resolved from Navigation settings.
            setActivePane?.(targetPane);
            
            // Do not force delayed focus here; it can steal focus back if the user
            // clicks another doc immediately after creation.
          }
        }}
      >
        New Document
      </button>
    </div>
  );
}

export default EditorPlaceholder;
